/*
 * Chamados: a lista e a abertura de um novo.
 *
 * A mesma tela para os dois lados. Quem atende ve a fila de todos, com os que
 * esperam resposta no topo; os demais veem os proprios chamados, com os que
 * tem resposta nova no topo.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PERMISSIONS,
  TICKET_CATEGORY_LABELS,
  TICKET_CATEGORY_VALUES,
  TICKET_PRIORITY_LABELS,
  TICKET_PRIORITY_VALUES,
  type PaginationMeta,
} from '@hotspot/shared';
import { ApiRequestError, apiList, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  INTERVALO_LISTA_MS,
  avisarMudancaNosChamados,
  useAtualizacaoPeriodica,
} from '../lib/tickets';
import { nomeDaLocalidade, type TicketListItemDto, type TicketLocationDto } from '../lib/types';
import { Alert, Button, Card, EmptyState, Field, PageHeader, Spinner } from '../components/ui';
import {
  CAMPO,
  PrazoAtual,
  PrioridadeBadge,
  SeletorDeImagens,
  SituacaoBadge,
  enviarAnexos,
  quando,
  textoDoLocal,
} from '../components/TicketBits';

const FILTROS = [
  { chave: 'abertos', rotulo: 'Em aberto' },
  { chave: 'finalizado', rotulo: 'Finalizados' },
  { chave: 'cancelado', rotulo: 'Cancelados' },
  { chave: '', rotulo: 'Todos' },
] as const;

export function TicketsPage() {
  const { can } = useAuth();
  const atende = can(PERMISSIONS.TICKET_MANAGE);

  const [filtro, setFiltro] = useState<string>('abertos');
  const [soOsMeus, setSoOsMeus] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [lista, setLista] = useState<TicketListItemDto[] | null>(null);
  const [meta, setMeta] = useState<PaginationMeta | undefined>();
  const [erro, setErro] = useState<string | null>(null);
  const [abrindo, setAbrindo] = useState(false);
  const [mikrotikId, setMikrotikId] = useState<number | null>(null);
  // Equipamentos e pracas que este usuario enxerga: servem ao filtro e ao formulario.
  const [locais, setLocais] = useState<TicketLocationDto[] | null>(null);

  useEffect(() => {
    apiRequest<TicketLocationDto[]>('/admin/tickets/locations')
      .then(setLocais)
      .catch(() => setLocais([]));
  }, []);

  // Resposta de uma consulta antiga (filtro trocado no meio) nao pode
  // sobrescrever a da atual.
  const ultimaConsulta = useRef(0);

  const carregar = useCallback(
    async (silencioso = false) => {
      const esta = ++ultimaConsulta.current;
      const partes = [`page=${pagina}`, `todos=${!soOsMeus}`];
      if (filtro) partes.push(`status=${filtro}`);
      if (mikrotikId !== null) partes.push(`mikrotikId=${mikrotikId}`);

      try {
        const { data, meta: novaMeta } = await apiList<TicketListItemDto>(
          `/admin/tickets?${partes.join('&')}`,
        );
        if (esta !== ultimaConsulta.current) return;
        setLista(data);
        setMeta(novaMeta);
        setErro(null);
      } catch (caught) {
        if (silencioso || esta !== ultimaConsulta.current) return;
        setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar');
      }
    },
    [filtro, soOsMeus, pagina, atende, mikrotikId],
  );

  useEffect(() => {
    setLista(null);
    setErro(null);
    void carregar();
  }, [carregar]);

  // Sem spinner: a lista se atualiza no lugar, sem piscar.
  useAtualizacaoPeriodica(() => void carregar(true), INTERVALO_LISTA_MS);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Chamados"
        description={
          atende
            ? 'Pedidos de ajuda de quem usa o painel. Os que esperam sua resposta aparecem primeiro.'
            : 'Abra um chamado e acompanhe a resposta por aqui. Aparecem os seus e os dos equipamentos da sua equipe.'
        }
        action={
          <>
            <Link
              to="/chamados/relatorio"
              className="inline-flex items-center rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-xs ring-1 ring-slate-200 ring-inset hover:bg-slate-50"
            >
              Relatório e prazos
            </Link>
            {abrindo ? null : <Button onClick={() => setAbrindo(true)}>Novo chamado</Button>}
          </>
        }
      />

      {abrindo ? <NovoChamado locais={locais ?? []} onCancelar={() => setAbrindo(false)} /> : null}

      <Card>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm shadow-sm">
            {FILTROS.map((opcao) => (
              <button
                key={opcao.chave}
                type="button"
                onClick={() => {
                  setFiltro(opcao.chave);
                  setPagina(1);
                }}
                className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                  filtro === opcao.chave
                    ? 'bg-brand-600 text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {opcao.rotulo}
              </button>
            ))}
          </div>

          {/* A equipe tambem ve os dos colegas: todo mundo pode querer so os seus. */}
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={soOsMeus}
              onChange={(evento) => {
                setSoOsMeus(evento.target.checked);
                setPagina(1);
              }}
              className="size-4 rounded border-slate-300 text-brand-600"
            />
            Só os que eu abri
          </label>

          {(locais?.length ?? 0) > 1 ? (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Equipamento
              <select
                value={mikrotikId ?? ''}
                onChange={(evento) => {
                  setMikrotikId(evento.target.value ? Number(evento.target.value) : null);
                  setPagina(1);
                }}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm"
              >
                <option value="">Todos</option>
                {locais!.map((local) => (
                  <option key={local.mikrotikId} value={local.mikrotikId}>
                    {local.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        {erro ? (
          <Alert>{erro}</Alert>
        ) : !lista ? (
          <Spinner label="Carregando os chamados..." />
        ) : lista.length === 0 ? (
          <EmptyState
            title={filtro === 'abertos' ? 'Nenhum chamado em aberto' : 'Nenhum chamado aqui'}
            description={
              atende
                ? 'Quando alguém abrir um chamado, ele aparece nesta lista.'
                : 'Use "Novo chamado" para pedir ajuda.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold tracking-wide text-slate-500 uppercase">
                    <th className="py-2 pr-3">Chamado</th>
                    <th className="py-2 pr-3">Praça</th>
                    <th className="py-2 pr-3">Situação</th>
                    <th className="py-2 pr-3">Prioridade</th>
                    <th className="py-2 pr-3">Prazo</th>
                    <th className="py-2 pr-3">Aberto por</th>
                    <th className="py-2 pr-3">Última atividade</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((chamado) => (
                    <tr key={chamado.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="py-2.5 pr-3">
                        <Link
                          to={`/chamados/${chamado.id}`}
                          className="flex items-center gap-2 font-medium text-slate-900 hover:text-brand-700"
                        >
                          {chamado.precisaAtencao ? (
                            <span
                              className="size-2 shrink-0 rounded-full bg-brand-500"
                              title={atende ? 'Esperando resposta' : 'Resposta nova'}
                            />
                          ) : null}
                          <span className="text-slate-400 tabular-nums">#{chamado.id}</span>
                          <span className="truncate">{chamado.title}</span>
                        </Link>
                        <span className="text-xs text-slate-500">
                          {TICKET_CATEGORY_LABELS[
                            chamado.category as keyof typeof TICKET_CATEGORY_LABELS
                          ] ?? chamado.category}
                          {chamado.sla.reChamadoDe ? (
                            <span className="ml-1.5 font-semibold text-amber-700">
                              · re-chamado do #{chamado.sla.reChamadoDe}
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-slate-700">
                        {textoDoLocal(chamado.local) ?? <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-2.5 pr-3">
                        <SituacaoBadge status={chamado.status} />
                      </td>
                      <td className="py-2.5 pr-3">
                        <PrioridadeBadge priority={chamado.priority} />
                      </td>
                      <td className="py-2.5 pr-3">
                        <PrazoAtual sla={chamado.sla} />
                      </td>
                      <td className="py-2.5 pr-3 text-slate-700">
                        {chamado.createdBy?.name ?? 'Usuário removido'}
                      </td>
                      <td className="py-2.5 pr-3 whitespace-nowrap text-slate-500">
                        {quando(chamado.lastMessageAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {meta && meta.totalPages > 1 ? (
              <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
                <span>
                  Página {meta.page} de {meta.totalPages} · {meta.total} chamados
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={meta.page <= 1}
                    onClick={() => setPagina((atual) => atual - 1)}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={meta.page >= meta.totalPages}
                    onClick={() => setPagina((atual) => atual + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}

function NovoChamado({
  locais,
  onCancelar,
}: {
  locais: TicketLocationDto[];
  onCancelar: () => void;
}) {
  const navigate = useNavigate();
  // Com um equipamento so, ja vem escolhido: perguntar o obvio e atrito.
  const [equipamentoId, setEquipamentoId] = useState<number | null>(
    locais.length === 1 ? locais[0]!.mikrotikId : null,
  );
  const [vlan, setVlan] = useState<string>('');
  const [titulo, setTitulo] = useState('');
  const [categoria, setCategoria] = useState<string>('problema');
  const [prioridade, setPrioridade] = useState<string>('normal');
  const [descricao, setDescricao] = useState('');
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const equipamentoSelecionado = locais.find((local) => local.mikrotikId === equipamentoId);
  // Mesma regra da API: problema acontece em algum lugar.
  const localObrigatorio =
    (categoria === 'problema' || categoria === 'troca_equipamento') && locais.length > 0;

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    setEnviando(true);
    setErro(null);

    try {
      const criado = await apiRequest<{ id: number; messageId: number }>('/admin/tickets', {
        method: 'POST',
        body: {
          title: titulo,
          category: categoria,
          priority: prioridade,
          body: descricao,
          ...(equipamentoId !== null ? { mikrotikId: equipamentoId } : {}),
          ...(equipamentoId !== null && vlan ? { vlan } : {}),
        },
      });
      const falhas = await enviarAnexos(criado.id, criado.messageId, arquivos);
      avisarMudancaNosChamados();
      navigate(`/chamados/${criado.id}`, { state: { falhasDeAnexo: falhas } });
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao abrir');
      setEnviando(false);
    }
  }

  return (
    <Card title="Novo chamado">
      <form onSubmit={enviar} className="space-y-4">
        {erro ? <Alert>{erro}</Alert> : null}

        <Field
          label="Assunto"
          value={titulo}
          onChange={(evento) => setTitulo(evento.target.value)}
          placeholder="Ex.: clientes da Praça Central não conseguem conectar"
          required
          minLength={3}
          maxLength={120}
          disabled={enviando}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700">Tipo</span>
            <select
              value={categoria}
              onChange={(evento) => setCategoria(evento.target.value)}
              disabled={enviando}
              className={CAMPO}
            >
              {TICKET_CATEGORY_VALUES.map((valor) => (
                <option key={valor} value={valor}>
                  {TICKET_CATEGORY_LABELS[valor]}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700">Prioridade</span>
            <select
              value={prioridade}
              onChange={(evento) => setPrioridade(evento.target.value)}
              disabled={enviando}
              className={CAMPO}
            >
              {TICKET_PRIORITY_VALUES.map((valor) => (
                <option key={valor} value={valor}>
                  {TICKET_PRIORITY_LABELS[valor]}
                </option>
              ))}
            </select>
          </label>
        </div>

        {locais.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">
                Equipamento{localObrigatorio ? '' : ' (opcional)'}
              </span>
              <select
                value={equipamentoId ?? ''}
                onChange={(evento) => {
                  setEquipamentoId(evento.target.value ? Number(evento.target.value) : null);
                  setVlan('');
                }}
                required={localObrigatorio}
                disabled={enviando}
                className={CAMPO}
              >
                <option value="">
                  {localObrigatorio ? 'Escolha o equipamento' : 'Não se aplica'}
                </option>
                {locais.map((local) => (
                  <option key={local.mikrotikId} value={local.mikrotikId}>
                    {local.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">Praça (VLAN)</span>
              <select
                value={vlan}
                onChange={(evento) => setVlan(evento.target.value)}
                disabled={enviando || !equipamentoSelecionado}
                className={CAMPO}
              >
                <option value="">
                  {equipamentoSelecionado ? 'Todas / não sei' : 'Escolha o equipamento antes'}
                </option>
                {(equipamentoSelecionado?.vlans ?? []).map((nome) => (
                  <option key={nome} value={nome}>
                    {nomeDaLocalidade(nome)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}

        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-slate-700">Descrição</span>
          <textarea
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
            rows={6}
            required
            maxLength={5000}
            disabled={enviando}
            placeholder="O que aconteceu, onde (equipamento, praça) e desde quando. Um print ajuda muito."
            className={CAMPO}
          />
        </label>

        <SeletorDeImagens arquivos={arquivos} onChange={setArquivos} disabled={enviando} />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancelar} disabled={enviando}>
            Cancelar
          </Button>
          <Button type="submit" disabled={enviando || !titulo.trim() || !descricao.trim()}>
            {enviando ? 'Enviando...' : 'Abrir chamado'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
